import { createFileRoute, Link } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';
import { useState } from 'react';
import { Card, CardContent } from '@cortex/web-design-system/components/ui/Card';
import { TabButton, TabGroup } from '../components/ui/Tabs/index.js';
import { ProtectedRoute } from '../components/auth/index.js';
import { useAuth } from '../hooks/useAuth.js';
import { ErrorState } from '../components/feedback/ErrorState/index.js';
import {
  usePmoOverdueProjects,
  usePmoUpcomingProjects,
  usePmoReleaseLeaks,
  usePmoAssignLeaks,
} from '../hooks/usePmoBoard.js';
import { DeadlineEditModal } from '../components/features/project/DeadlineEditModal.js';
import { SlackChannelButton } from '../components/features/project/SlackChannelButton.js';
import { ProgressBar } from '../components/features/project/ProjectTable.js';
import { BACKLOG_BASE } from '../components/features/project/ChildTicketsModal.js';
import { PerformanceAggView } from '../components/features/project/PerformanceAggView.js';
import { ProjectAssignButton } from '../components/features/project-assign/ProjectAssignButton.js';
import type { PmoOverdueProject, PmoLeakProject, PmoLeakCategory } from '../types/project.js';

/** @graph-connects none */
const UPCOMING_DAYS = 3;

/** リリース日漏れビューで表示する leak カテゴリの並び順 */
/** @graph-connects none */
const RELEASE_LEAK_CATEGORIES: PmoLeakCategory[] = ['parent_done_no_release', 'release_child_done'];

/** アサイン日漏れビューで表示する leak カテゴリの並び順 */
/** @graph-connects none */
const ASSIGN_LEAK_CATEGORIES: PmoLeakCategory[] = ['released_no_assign', 'active_no_assign'];

/**
 * @graph-node {Page}
 * @graph-stack product-innovation-lab
 * @graph-domain Engineering
 * @graph-business PMO看板ページ。「期限超過」「期限直近」「リリース日漏れ管理」「アサイン日漏れ管理」「実績集計」の5タブを切り替えて表示。期限系2タブは行クリックで期限編集モーダルを開きBacklogへ書き戻し、漏れ管理2タブは行クリックで対象Backlogチケットを直接開く。VIEW_PMO_BOARD を持たず VIEW_PMO_PERFORMANCE_AGG だけ持つ PDG の team_leader には実績集計のみを単独ページとして表示する
 * @graph-connects usePmoOverdueProjects [calls] PMO期限切れ案件取得
 * @graph-connects usePmoUpcomingProjects [calls] PMO直近期限案件取得
 * @graph-connects usePmoReleaseLeaks [calls] リリース日漏れチケット取得
 * @graph-connects usePmoAssignLeaks [calls] アサイン日漏れチケット取得
 * @graph-connects DeadlineEditModal [calls] 期限編集モーダル描画
 * @graph-connects SlackChannelButton [calls] Slack channelボタン描画
 * @graph-connects ProjectAssignButton [calls] 案件アサインボタン描画（ASSIGN_PROJECT 権限保持者のみ）
 */
export const Route = createFileRoute('/projects/pmo-board')({
  component: PmoBoardPage,
});

type DeadlineView = 'overdue' | 'upcoming';
type LeakView = 'releaseLeaks' | 'assignLeaks';
type View = DeadlineView | LeakView | 'performanceAgg';

/** @graph-connects none */
function isLeakView(view: View): view is LeakView {
  return view === 'releaseLeaks' || view === 'assignLeaks';
}

/** @graph-connects none */
function formatDate(value: string | null): string {
  if (!value) return '-';
  return value.slice(0, 10);
}

/** 結合期限を過ぎてからの経過日数（マイナスなら未来） */
/** @graph-connects none */
function daysDiff(deadline: string | null): number | null {
  if (!deadline) return null;
  const d = new Date(deadline.slice(0, 10));
  if (Number.isNaN(d.getTime())) return null;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return Math.floor((today.getTime() - d.getTime()) / (1000 * 60 * 60 * 24));
}

/**
 * PMO看板ページ本体。
 *
 * **「実績集計」だけ閲覧範囲が広い。** PMOT / admin に加え PDG 全チームの team_leader が
 * `VIEW_PMO_PERFORMANCE_AGG` で入ってくる（`canAccessPage('pmoBoard')` もその OR）。
 * その人たちには実績集計タブ**だけ**を出す。他タブのデータ取得フックは `enabled: false` 相当に
 * できないので、`VIEW_PMO_BOARD` が無いときは呼ばずに済むよう early return で分岐する
 * （呼ぶと API 側の `assertPmoBoardAccess` が 403 を返し、無関係なエラー表示が出る）。
 * @graph-connects usePmoOverdueProjects [calls] PMO期限切れ案件取得
 * @graph-connects useAuth [calls] VIEW_PMO_BOARD / VIEW_PMO_PERFORMANCE_AGG の権限判定
 */
function PmoBoardPage() {
  const { can } = useAuth();
  // 実績集計しか権限が無い人（PDG の team_leader）はタブを切り替えられないので、
  // 他タブのフックを一切呼ばない別コンポーネントへ分ける。
  return (
    <ProtectedRoute pageKey="pmoBoard">
      {can('VIEW_PMO_BOARD') ? <PmoBoardFullPage /> : <PerformanceAggOnlyPage />}
    </ProtectedRoute>
  );
}

/**
 * 実績集計タブのみのページ（`VIEW_PMO_PERFORMANCE_AGG` だけを持つ PDG の team_leader 向け）。
 * @graph-connects PerformanceAggView [calls] 実績集計ビュー描画
 */
function PerformanceAggOnlyPage() {
  const { t } = useTranslation();
  return (
    <div className="space-y-6 animate-fade-in">
      <div className="animate-content-reveal">
        <h1 className="text-2xl font-bold text-[var(--color-text-primary)]">
          {t('pmoBoard.tabs.performanceAgg')}
        </h1>
        <p className="mt-1 text-[var(--color-text-secondary)]">
          {t('pmoBoard.performanceAgg.pageDescription')}
        </p>
      </div>
      <PerformanceAggView />
    </div>
  );
}

/** PMO看板の全タブ版（`VIEW_PMO_BOARD` 保持者）。 */
/** @graph-connects usePmoOverdueProjects [calls] PMO期限切れ案件取得 */
function PmoBoardFullPage() {
  const { t } = useTranslation();
  const [view, setView] = useState<View>('overdue');
  const [selected, setSelected] = useState<PmoOverdueProject | null>(null);

  const overdue = usePmoOverdueProjects();
  const upcoming = usePmoUpcomingProjects(UPCOMING_DAYS);
  const releaseLeaks = usePmoReleaseLeaks();
  const assignLeaks = usePmoAssignLeaks();

  return (
    <>
      <div className="space-y-6 animate-fade-in">
        <div className="animate-content-reveal">
          <h1 className="text-2xl font-bold text-[var(--color-text-primary)]">
            {t('pmoBoard.title')}
          </h1>
          <p className="mt-1 text-[var(--color-text-secondary)]">{t('pmoBoard.description')}</p>
        </div>

        {/* タブスイッチ（TabGroup 既定のスライドピル）とアサインボタンを同じ行に並べる。
            ボタンを見出し行へ置くとタブ行より 1 段高くなり、右側だけ宙に浮いて見える。
            ボタンの高さ（44px）はタブスイッチと同値なので、下端揃えで上下とも揃う。 */}
        <div className="flex items-end justify-between gap-3">
          <TabGroup ariaLabel={t('pmoBoard.viewSwitchLabel')} className="animate-content-reveal">
            <TabButton
              active={view === 'overdue'}
              onClick={() => {
                setView('overdue');
              }}
              label={t('pmoBoard.tabs.overdue')}
              count={overdue.data?.count}
              tone="danger"
            />
            <TabButton
              active={view === 'upcoming'}
              onClick={() => {
                setView('upcoming');
              }}
              label={t('pmoBoard.tabs.upcoming', { days: UPCOMING_DAYS })}
              count={upcoming.data?.count}
              tone="warning"
            />
            <TabButton
              active={view === 'releaseLeaks'}
              onClick={() => {
                setView('releaseLeaks');
              }}
              label={t('pmoBoard.tabs.releaseLeaks')}
              count={releaseLeaks.data?.count}
              tone="danger"
            />
            <TabButton
              active={view === 'assignLeaks'}
              onClick={() => {
                setView('assignLeaks');
              }}
              label={t('pmoBoard.tabs.assignLeaks')}
              count={assignLeaks.data?.count}
              tone="info"
            />
            <TabButton
              active={view === 'performanceAgg'}
              onClick={() => {
                setView('performanceAgg');
              }}
              label={t('pmoBoard.tabs.performanceAgg')}
            />
          </TabGroup>
          {/* アサイン権限（PMOT / PDG の team_leader / 管理者）が無いときは描画されない。 */}
          <ProjectAssignButton />
        </div>

        {view === 'performanceAgg' ? (
          <PerformanceAggView />
        ) : isLeakView(view) ? (
          <LeakSection query={view === 'releaseLeaks' ? releaseLeaks : assignLeaks} view={view} />
        ) : (
          <DeadlineSection
            view={view}
            query={view === 'overdue' ? overdue : upcoming}
            onSelect={setSelected}
          />
        )}
      </div>

      <DeadlineEditModal
        open={selected !== null}
        onClose={() => {
          setSelected(null);
        }}
        project={selected}
      />
    </>
  );
}

// ============================================================
// 期限管理ビュー（期限超過 / 期限直近）
// ============================================================

interface DeadlineSectionProps {
  view: DeadlineView;
  query: ReturnType<typeof usePmoOverdueProjects>;
  onSelect: (project: PmoOverdueProject) => void;
}

/** @graph-connects none */
function DeadlineSection({ view, query, onSelect }: DeadlineSectionProps) {
  const { t } = useTranslation();
  const { data, isLoading, error, refetch } = query;

  const blockTitle =
    view === 'overdue'
      ? t('pmoBoard.overdueBlock.title')
      : t('pmoBoard.upcomingBlock.title', { days: UPCOMING_DAYS });
  const blockSubtitle =
    view === 'overdue'
      ? t('pmoBoard.overdueBlock.subtitle')
      : t('pmoBoard.upcomingBlock.subtitle', { days: UPCOMING_DAYS });
  const emptyMessage =
    view === 'overdue' ? t('pmoBoard.overdueBlock.empty') : t('pmoBoard.upcomingBlock.empty');

  return (
    <>
      {error && (
        <ErrorState
          title={t('feedback.errorTitle')}
          message={t('feedback.errorMessage')}
          onRetry={() => {
            void refetch();
          }}
        />
      )}

      <Card className="animate-content-reveal">
        <div className="px-6 pt-5 pb-3 flex items-center justify-between">
          <div>
            <h3 className="text-lg font-semibold text-[var(--color-text-primary)]">{blockTitle}</h3>
            <p className="text-xs text-[var(--color-text-muted)] mt-0.5">{blockSubtitle}</p>
          </div>
          {data && (
            <span className="text-sm text-[var(--color-text-muted)]">
              {t('pmoBoard.overdueBlock.count', { count: data.count })}
            </span>
          )}
        </div>

        <CardContent>
          {isLoading ? (
            <LoadingRows />
          ) : data && data.count > 0 ? (
            <DeadlineTable view={view} rows={data.data} onSelect={onSelect} />
          ) : (
            <p className="py-12 text-center text-sm text-[var(--color-text-muted)]">
              {emptyMessage}
            </p>
          )}
        </CardContent>
      </Card>
    </>
  );
}

/** @graph-connects none */
function LoadingRows() {
  return (
    <div className="space-y-2 py-2">
      {[1, 2, 3, 4, 5].map((i) => (
        <div key={i} className="h-12 rounded bg-[var(--color-bg-elevated)] animate-pulse" />
      ))}
    </div>
  );
}

interface DeadlineTableProps {
  view: DeadlineView;
  rows: PmoOverdueProject[];
  onSelect: (project: PmoOverdueProject) => void;
}

/** @graph-connects SlackChannelButton [calls] Slack channel ボタン描画 */
function DeadlineTable({ view, rows, onSelect }: DeadlineTableProps) {
  const { t } = useTranslation();
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead>
          <tr className="text-left border-b border-[var(--color-border)] text-[var(--color-text-muted)]">
            <th className="py-2 px-3 font-medium">{t('pmoBoard.columns.issueKey')}</th>
            <th className="py-2 px-3 font-medium">{t('pmoBoard.columns.summary')}</th>
            <th className="py-2 px-3 font-medium">{t('pmoBoard.columns.integrationDeadline')}</th>
            <th className="py-2 px-3 font-medium w-40">{t('pmoBoard.columns.progress')}</th>
            <th className="py-2 px-3 font-medium">{t('pmoBoard.columns.pl')}</th>
            <th className="py-2 px-3 font-medium">{t('pmoBoard.columns.mainWorker')}</th>
            <th className="py-2 px-3 font-medium">{t('pmoBoard.columns.devLeader')}</th>
            <th className="py-2 px-3 font-medium text-right">{t('pmoBoard.columns.actions')}</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((p) => {
            const diff = daysDiff(p.dueDate);
            const isOverdue = view === 'overdue';
            const deadlineClass = isOverdue
              ? 'text-[var(--color-error)]'
              : 'text-[var(--color-warning)]';
            return (
              <tr
                key={p.issueKey}
                onClick={() => {
                  onSelect(p);
                }}
                className="border-b border-[var(--color-border)] cursor-pointer hover:bg-[var(--color-bg-hover)] transition-colors"
              >
                <td className="py-2 px-3">
                  <Link
                    to="/projects/$pjKey"
                    params={{ pjKey: p.issueKey }}
                    onClick={(e) => {
                      e.stopPropagation();
                    }}
                    className="text-[var(--color-accent-primary)] hover:underline"
                  >
                    {p.issueKey}
                  </Link>
                </td>
                <td className="py-2 px-3 max-w-md truncate" title={p.summary}>
                  {p.summary}
                </td>
                <td className="py-2 px-3 whitespace-nowrap">
                  <span className={`font-medium ${deadlineClass}`}>{formatDate(p.dueDate)}</span>
                  {diff !== null && (
                    <span className="ml-2 text-xs text-[var(--color-text-muted)]">
                      {isOverdue && diff > 0
                        ? `(${t('pmoBoard.daysOverdue', { days: diff })})`
                        : !isOverdue && diff <= 0
                          ? `(${t('pmoBoard.daysUntil', { days: -diff })})`
                          : ''}
                    </span>
                  )}
                </td>
                <td className="py-2 px-3">
                  <ProgressBar ratio={p.completionRatio} />
                </td>
                <td className="py-2 px-3 whitespace-nowrap">{p.assignee ?? '-'}</td>
                <td className="py-2 px-3 whitespace-nowrap">{p.mainWorker ?? '-'}</td>
                <td className="py-2 px-3 whitespace-nowrap">{p.devLeader ?? '-'}</td>
                <td className="py-2 px-3 text-right">
                  <SlackChannelButton
                    pjChannelId={p.pjSlackChannelId}
                    devChannelId={p.devSlackChannelId}
                  />
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

// ============================================================
// 漏れ管理ビュー（リリース日漏れ / アサイン日漏れ）
// ============================================================

interface LeakSectionProps {
  view: LeakView;
  query: ReturnType<typeof usePmoReleaseLeaks>;
}

/** @graph-connects none */
function LeakSection({ view, query }: LeakSectionProps) {
  const { t } = useTranslation();
  const { data, isLoading, error, refetch } = query;
  const categories = view === 'releaseLeaks' ? RELEASE_LEAK_CATEGORIES : ASSIGN_LEAK_CATEGORIES;

  if (error) {
    return (
      <ErrorState
        title={t('feedback.errorTitle')}
        message={t('feedback.errorMessage')}
        onRetry={() => {
          void refetch();
        }}
      />
    );
  }

  if (isLoading) {
    return (
      <Card className="animate-content-reveal">
        <CardContent>
          <LoadingRows />
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      {categories.map((category) => {
        const rows = data?.data.filter((row) => row.leakCategory === category) ?? [];
        return <LeakCategoryBlock key={category} category={category} rows={rows} />;
      })}
    </div>
  );
}

interface LeakCategoryBlockProps {
  category: PmoLeakCategory;
  rows: PmoLeakProject[];
}

/** @graph-connects none */
function LeakCategoryBlock({ category, rows }: LeakCategoryBlockProps) {
  const { t } = useTranslation();
  return (
    <Card className="animate-content-reveal">
      <div className="px-6 pt-5 pb-3 flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold text-[var(--color-text-primary)]">
            {t(`pmoBoard.leakCategories.${category}.title`)}
          </h3>
          <p className="text-xs text-[var(--color-text-muted)] mt-0.5">
            {t(`pmoBoard.leakCategories.${category}.subtitle`)}
          </p>
        </div>
        <span className="text-sm text-[var(--color-text-muted)]">
          {t('pmoBoard.overdueBlock.count', { count: rows.length })}
        </span>
      </div>
      <CardContent>
        {rows.length > 0 ? (
          <LeakTable rows={rows} />
        ) : (
          <p className="py-12 text-center text-sm text-[var(--color-text-muted)]">
            {t('pmoBoard.leakBlock.empty')}
          </p>
        )}
      </CardContent>
    </Card>
  );
}

interface LeakTableProps {
  rows: PmoLeakProject[];
}

/** @graph-connects none */
function LeakTable({ rows }: LeakTableProps) {
  const { t } = useTranslation();
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead>
          <tr className="text-left border-b border-[var(--color-border)] text-[var(--color-text-muted)]">
            <th className="py-2 px-3 font-medium">{t('pmoBoard.columns.issueKey')}</th>
            <th className="py-2 px-3 font-medium">{t('pmoBoard.columns.summary')}</th>
            <th className="py-2 px-3 font-medium">{t('pmoBoard.columns.issueType')}</th>
            <th className="py-2 px-3 font-medium">{t('pmoBoard.columns.status')}</th>
            <th className="py-2 px-3 font-medium">{t('pmoBoard.columns.assignee')}</th>
            <th className="py-2 px-3 font-medium">{t('pmoBoard.columns.releasedAt')}</th>
            <th className="py-2 px-3 font-medium">{t('pmoBoard.columns.updatedAt')}</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((p) => (
            <tr
              key={p.issueKey}
              onClick={() => {
                window.open(`${BACKLOG_BASE}${p.issueKey}`, '_blank', 'noopener,noreferrer');
              }}
              className="border-b border-[var(--color-border)] cursor-pointer hover:bg-[var(--color-bg-hover)] transition-colors"
            >
              <td className="py-2 px-3">
                <a
                  href={`${BACKLOG_BASE}${p.issueKey}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={(e) => {
                    e.stopPropagation();
                  }}
                  className="text-[var(--color-accent-primary)] hover:underline"
                >
                  {p.issueKey}
                </a>
              </td>
              <td className="py-2 px-3 max-w-md truncate" title={p.summary}>
                {p.summary}
              </td>
              <td className="py-2 px-3 whitespace-nowrap">{p.issueTypeName}</td>
              <td className="py-2 px-3 whitespace-nowrap">{p.status}</td>
              <td className="py-2 px-3 whitespace-nowrap">{p.mainWorker ?? p.assignee ?? '-'}</td>
              <td className="py-2 px-3 whitespace-nowrap">{formatDate(p.releasedAt)}</td>
              <td className="py-2 px-3 whitespace-nowrap text-[var(--color-text-muted)]">
                {formatDate(p.updatedAt)}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
